import { useState, useMemo } from 'react';
import { ArrowLeft, Plus, FileStack } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePropertyArticles } from '@/hooks/inventory/usePropertyArticles';
import { useProperties } from '@/hooks/useProperties';
import PropertyArticlesTable from '../tables/PropertyArticlesTable';
import AddItemToPropertyModal from '../modals/AddItemToPropertyModal';
import AddFromTemplateModal from '../modals/AddFromTemplateModal';
import UpdateEntryModal from '../modals/UpdateEntryModal';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PropertyArticlesViewProps {
  propertyId: string;
  onBack: () => void;
}

export default function PropertyArticlesView({ propertyId, onBack }: PropertyArticlesViewProps) {
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const { entries, isLoading } = usePropertyArticles(propertyId);
  const propertiesQuery = useProperties();
  const property = propertiesQuery.data?.find(p => p.id === propertyId);

  // Group entries by category
  const entriesByCategory = useMemo(() => {
    const grouped = new Map<string, typeof entries>();
    
    entries.forEach((entry) => {
      const categoryId = entry.item?.category?.id || 'uncategorized';
      const categoryName = entry.item?.category?.name || 'Sin categoría';
      const sortOrder = entry.item?.category?.sort_order ?? 999;
      const key = `${sortOrder}|||${categoryId}|||${categoryName}`;
      
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(entry);
    });

    // Sort by category sort_order, then sort entries within each category by item.sort_order
    return Array.from(grouped.entries())
      .sort((a, b) => {
        const [sortA] = a[0].split('|||');
        const [sortB] = b[0].split('|||');
        return Number(sortA) - Number(sortB);
      })
      .map(([key, categoryEntries]) => {
        const [, categoryId, categoryName] = key.split('|||');
        // Sort entries by item sort_order within each category
        const sortedEntries = [...categoryEntries].sort(
          (a, b) => (a.item?.sort_order ?? 0) - (b.item?.sort_order ?? 0)
        );
        return { categoryId, categoryName, entries: sortedEntries };
      });
  }, [entries]);

  const handleUpdateEntry = (entryId: string) => {
    setSelectedEntryId(entryId);
  };

  const selectedEntry = entries.find((e) => e.id === selectedEntryId);
  const propertyName = property?.shortName || property?.title || 'Propiedad';
  const hasNoArticles = entries.length === 0;

  if (propertiesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-xl font-semibold">Artículos de {propertyName}</h2>
            <p className="text-sm text-muted-foreground">
              {entries.length} artículo{entries.length !== 1 ? 's' : ''} asignados
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasNoArticles && (
            <Button
              variant="outline"
              onClick={() => setIsTemplateModalOpen(true)}
            >
              <FileStack className="h-4 w-4 mr-2" />
              Agregar desde plantilla
            </Button>
          )}
          <Button
            onClick={() => setIsAddItemModalOpen(true)}
            className="bg-brand-primary hover:bg-brand-primary-hover"
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar artículo
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-lg">
          <p>Esta propiedad no tiene artículos</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsTemplateModalOpen(true)}
            >
              <FileStack className="h-4 w-4 mr-2" />
              Agregar desde plantilla
            </Button>
            <Button
              variant="link"
              onClick={() => setIsAddItemModalOpen(true)}
            >
              Agregar artículo individual
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {entriesByCategory.map(({ categoryId, categoryName, entries }) => (
            <div key={categoryId} className="space-y-2">
              <h3 className="font-medium text-sm text-muted-foreground">
                {categoryName} ({entries.length})
              </h3>
              <PropertyArticlesTable
                entries={entries}
                propertyId={propertyId}
                onUpdateEntry={handleUpdateEntry}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AddItemToPropertyModal
        open={isAddItemModalOpen}
        onOpenChange={setIsAddItemModalOpen}
        propertyId={propertyId}
        existingItemIds={entries.map((e) => e.item_id)}
      />

      <AddFromTemplateModal
        open={isTemplateModalOpen}
        onOpenChange={setIsTemplateModalOpen}
        propertyId={propertyId}
      />

      {selectedEntry && (
        <UpdateEntryModal
          open={!!selectedEntryId}
          onOpenChange={(open) => !open && setSelectedEntryId(null)}
          entry={selectedEntry}
        />
      )}
    </div>
  );
}
